import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { InstructionsDialog } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsDialog";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type TPresentationInstructionsModalProps = { open: boolean; onClose: () => void };

export function PresentationInstructionsModal({ open, onClose }: TPresentationInstructionsModalProps) {
  const { t } = useTranslation("projectHome");
  const { selectedProject, updateProject } = useProjectStore();
  const { isLabs } = useMeStore();

  const [projectInstructions, setProjectInstructions] = useState("");
  const [chaptersInstructions, setChaptersInstructions] = useState<Record<string, string>>({});
  const [presentationPrompt, setPresentationPrompt] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const wasOpenRef = useRef(false);

  const chapters = useMemo(
    () =>
      selectedProject?.projectChapters
        ?.filter((ch) => {
          if (ch.tag === "iq_generated_appendix") return false;
          if (!isLabs && ch.isHidden) return false;
          return true;
        })
        .map((ch) => ({
          slug: ch.projectTemplateChapter.slug,
          name: ch.projectTemplateChapter.name,
          icon: ch.projectTemplateChapter.icon
        })) ?? [],
    [selectedProject?.projectChapters, isLabs]
  );

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    if (!selectedProject) return;
    setProjectInstructions(selectedProject.projectInstructions ?? "");
    setPresentationPrompt(selectedProject.presentationPrompt ?? "");
    setChaptersInstructions(
      selectedProject.projectChapters?.reduce((acc, ch) => {
        acc[ch.projectTemplateChapter.slug] = ch.chapterInstructions ?? "";
        return acc;
      }, {} as Record<string, string>) ?? {}
    );
  }, [open, selectedProject]);

  const handleSave = useCallback(async () => {
    if (!selectedProject?.id) return;
    setIsSaving(true);
    const [, error] = await tryCatch(
      updateProject(selectedProject.id, {
        projectInstructions,
        chaptersInstructions,
        presentationPrompt
      })
    );
    setIsSaving(false);
    if (error) {
      toast.error(t("instructionsModal.saveFailed"));
      return;
    }
    toast.success(t("instructionsModal.saved"));
    onClose();
  }, [selectedProject?.id, updateProject, projectInstructions, chaptersInstructions, presentationPrompt, onClose, t]);

  return (
    <InstructionsDialog
      open={open}
      onClose={onClose}
      onSave={handleSave}
      isSaving={isSaving}
      chapters={chapters}
      projectInstructions={projectInstructions}
      chaptersInstructions={chaptersInstructions}
      presentationPrompt={presentationPrompt}
      onProjectInstructionsChange={(pi, ci) => {
        setProjectInstructions(pi);
        setChaptersInstructions(ci);
      }}
      onPresentationPromptChange={setPresentationPrompt}
      defaultSection="outline-instructions"
    />
  );
}
